export default function JavaVEditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - Java Editor &amp; Code Runner
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for Java Editor &amp;
            Code Runner.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I write and run a Java program?
              </h3>
              <p className="mt-1">
                Tap the + button to create a new file. Write your Java code in
                the editor, then tap the Run button (play icon) to compile and
                execute it. Output appears in the console panel below the
                editor. Compilation and execution happen entirely on your
                device — no internet connection is required to run code.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I install a Java library?
              </h3>
              <p className="mt-1">
                Open the <strong>Libraries</strong> tab from the bottom
                navigation. You can browse curated libraries (Apache Commons,
                Gson, OkHttp, Jsoup, and more) and install them with one tap,
                or use the Maven search to find any library from Maven Central
                by group and artifact. An internet connection is required for
                installation. Once installed, a library is added to your
                compile classpath and can be imported from any script.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I add my own JAR file?
              </h3>
              <p className="mt-1">
                Open the Libraries tab, tap the Maven manager, then choose
                &quot;Import JAR&quot; to pick a JAR from your device storage.
                The JAR is added to your compile classpath and is available to
                all your scripts. You can remove imported JARs at any time.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I use Notebook Mode?
              </h3>
              <p className="mt-1">
                Open the Notebook tab from the bottom navigation, or switch to
                Notebook Mode from the editor menu. Each cell compiles and
                runs independently — tap the Run button on a cell to execute
                it and see its output inline. Notebooks are great for trying
                out small snippets, building up a script incrementally, or
                teaching Java step by step.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I run a web server from my Java code?
              </h3>
              <p className="mt-1">
                Write a class that extends <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">WebApp</code>{" "}
                and return HTML, JSON, or any response from its handler. Tap
                Run and the app will start a local server on your device and
                print the address (for example{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  http://localhost:8080
                </code>
                ) so you can open it in your browser. Tap Stop to shut the
                server down.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I manage my projects?
              </h3>
              <p className="mt-1">
                Open the Projects tab from the bottom navigation. You can
                create new projects, rename them, move files between projects,
                and delete projects you no longer need. All project data is
                stored locally on your device.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I use the Examples Library?
              </h3>
              <p className="mt-1">
                From the home screen, scroll to the Java Examples section to
                browse built-in example programs covering core language
                features, collections, I/O, concurrency, web servers, and
                more. Tap any example to open it in the editor and run or
                modify it.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Which Java version does the app use?
              </h3>
              <p className="mt-1">
                The app compiles your code as Java 17, so modern language
                features such as records, sealed classes, pattern matching,
                switch expressions, and text blocks are all supported. Check
                Settings &gt; About for the exact runtime version in use.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why is a library not available to install?
              </h3>
              <p className="mt-1">
                Most pure-Java libraries from Maven Central work out of the
                box. Libraries that depend on desktop JDK APIs not available
                on mobile, or on native platform code that is not packaged
                for Android, may fail to load. If a library you need is not
                working, email us and we will investigate.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. You can remove all
                ads with a one-time in-app purchase from Settings.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing. What do I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the app. Make sure you have the
                latest version from Google Play. If a specific program causes
                the crash, check for infinite loops or very large memory
                usage. Email us with your device model, OS version, app
                version, and steps to reproduce.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details in
            your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/javavedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/javavedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/javavedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/javavedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> Java Editor &amp; Code Runner
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.javavedit
            </li>
            <li>
              <strong>Platform:</strong> Android
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
